import { createRouter, createWebHistory } from 'vue-router';
import auth from '@/auth/index'
import store from '@/store'
import { DEFAULT_LANGUAGE } from '@/utils/constants'

const lang = localStorage.selectedLanguage || DEFAULT_LANGUAGE

export const routes = [
  {
    path: '/',
    redirect: `/${lang}`
  },
  {
    path: '/set-password/:token/:tenantId?',
    redirect: to => {
      const { token, tenantId } = to.params

      return tenantId
        ? `/${lang}/set-password/${token}/${tenantId}`
        : `/${lang}/set-password/${token}`
    },
    props: true
  },
  {
    path: '/:lang',
    component: () =>
      import(
        /* webpackChunkName: "layout-default" */ '@/layouts/MainLayout'
      ),
    children: [
      {
        path: 'login',
        name: 'login',
        meta: { requiresAuth: false },
        component: () =>
          import(/* webpackChunkName: "login" */ '@/pages/login/Login')
      },
      {
        path: 'forgot-password',
        name: 'forgot-password',
        meta: { requiresAuth: false },
        component: () =>
          import(
            /* webpackChunkName: "forgot-password" */ '@/pages/login/ForgotPassword'
          )
      },
      {
        path: 'set-password/:token/:tenantId?',
        name: 'set-password',
        meta: { requiresAuth: false },
        component: () =>
          import(/* webpackChunkName: "set-password" */ '@/pages/SetPassword'),
        props: true
      },
      {
        path: '',
        name: 'home',
        meta: { requiresAuth: true },
        component: () => import(/* webpackChunkName: "home" */ '@/pages/Home')
      },
      {
        path: 'profile',
        name: 'profile',
        meta: { requiresAuth: true },
        component: () =>
          import(/* webpackChunkName: "profile" */ '@/pages/profile/Profile')
      },
      {
        path: 'processes',
        component: () =>
          import(
            /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
          ),
        children: [
          {
            path: '',
            name: 'processes',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "processes" */ '@/pages/processes/Processes'
              )
          },
          {
            path: 'create',
            name: 'processCreate',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "process-create" */ '@/pages/processes/ProcessDetails'
              )
          },
          {
            path: 'edit/:id',
            name: 'processEdit',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "process-edit" */ '@/pages/processes/ProcessDetails'
              )
          },
          {
            path: 'copy/:id',
            name: 'processCopy',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "process-copy" */ '@/pages/processes/ProcessDetails'
              )
          },
          {
            path: 'restore',
            name: 'processRestore',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "process-restore" */ '@/pages/processes/ProcessDetails'
              )
          }
        ]
      },
      {
        path: 'flow',
        component: () =>
          import(
            /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
          ),
        children: [
          {
            path: '',
            name: 'flow',
            meta: { requiresAuth: true },
            component: () =>
              import(/* webpackChunkName: "flows" */ '@/pages/flow/Flows')
          },
          {
            path: 'create',
            name: 'flowCreate',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "flow-create" */ '@/pages/flow/FlowDetails'
              )
          },
          {
            path: 'edit/:id',
            name: 'flowEdit',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "flow-edit" */ '@/pages/flow/FlowDetails'
              )
          },
          {
            path: 'copy/:id',
            name: 'flowCopy',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "flow-copy" */ '@/pages/flow/FlowDetails'
              )
          },
          {
            path: 'restore',
            name: 'flowRestore',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "flow-restore" */ '@/pages/flow/FlowDetails'
              )
          }
        ]
      },
      {
        path: 'screen',
        component: () =>
          import(
            /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
          ),
        children: [
          {
            path: '',
            name: 'screen',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "screen" */ '@/pages/screen/Screens'
              )
          },
          {
            path: 'create',
            name: 'screenCreate',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "screen-create" */ '@/pages/screen/ScreenDetails'
              )
          },
          {
            path: 'edit/:id',
            name: 'screenEdit',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "screen-edit" */ '@/pages/screen/ScreenDetails'
              )
          },
          {
            path: 'copy/:id',
            name: 'screenCopy',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "screen-copy" */ '@/pages/screen/ScreenDetails'
              )
          },
          {
            path: 'restore',
            name: 'screenRestore',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "screen-restore" */ '@/pages/screen/ScreenDetails'
              )
          }
        ]
      },
      {
        path: 'applications',
        component: () =>
          import(
            /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
          ),
        children: [
          {
            path: '',
            name: 'applications',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "applications" */ '@/pages/applications/Applications'
              )
          },
          {
            path: 'create',
            name: 'applicationCreate',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "application-create" */ '@/pages/applications/ApplicationDetails'
              )
          },
          {
            path: 'edit/:id',
            name: 'applicationEdit',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "application-edit" */ '@/pages/applications/ApplicationDetails'
              )
          },
          {
            path: 'copy/:id',
            name: 'applicationCopy',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "application-copy" */ '@/pages/applications/ApplicationDetails'
              )
          },
          {
            path: 'restore',
            name: 'applicationRestore',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "application-restore" */ '@/pages/applications/ApplicationDetails'
              )
          }
        ]
      },
      {
        path: 'aiAgents',
        component: () =>
          import(
            /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
          ),
        children: [
          {
            path: '',
            name: 'ai-agents',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "ai-agents" */ '@/pages/ai-agents/AiAgents'
              )
          },
          {
            path: 'create',
            name: 'aiAgentCreate',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "ai-agents-create" */ '@/pages/ai-agents/AiAgentDetails'
              )
          },
          {
            path: 'edit/:id',
            name: 'aiAgentEdit',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "ai-agents-edit" */ '@/pages/ai-agents/AiAgentDetails'
              )
          },
          {
            path: 'copy/:id',
            name: 'aiAgentCopy',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "ai-agents-copy" */ '@/pages/ai-agents/AiAgentDetails'
              )
          },
          {
            path: 'restore',
            name: 'aiAgentRestore',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "ai-agents-restore" */ '@/pages/ai-agents/AiAgentDetails'
              )
          }
        ]
      },
      {
        path: 'credentials',
        component: () =>
          import(
            /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
          ),
        children: [
          {
            path: '',
            name: 'credentials',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "credentials" */ '@/pages/credentials/Credentials'
              )
          },
          {
            path: 'create/:type',
            name: 'credentialCreate',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "credential-create" */ '@/pages/credentials/CredentialDetails'
              )
          },
          {
            path: 'edit/:id',
            name: 'credentialEdit',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "credential-edit" */ '@/pages/credentials/CredentialDetails'
              )
          },
          {
            path: 'copy/:id',
            name: 'credentialCopy',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "credential-copy" */ '@/pages/credentials/CredentialDetails'
              )
          },
          {
            path: 'restore',
            name: 'credentialRestore',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "credential-restore" */ '@/pages/credentials/CredentialDetails'
              )
          }
        ]
      },
      {
        path: 'triggers',
        component: () =>
          import(
            /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
          ),
        children: [
          {
            path: 'cron',
            component: () =>
              import(
                /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
              ),
            children: [
              {
                path: '',
                name: 'cron',
                meta: { requiresAuth: true },
                component: () =>
                  import(/* webpackChunkName: "crons" */ '@/pages/cron/Crons')
              },
              {
                path: 'create',
                name: 'cronCreate',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "cron-create" */ '@/pages/cron/CronDetails'
                  )
              },
              {
                path: 'edit/:id',
                name: 'cronEdit',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "cron-edit" */ '@/pages/cron/CronDetails'
                  )
              },
              {
                path: 'copy/:id',
                name: 'cronCopy',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "cron-copy" */ '@/pages/cron/CronDetails'
                  )
              },
              {
                path: 'restore',
                name: 'cronRestore',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "cron-restore" */ '@/pages/cron/CronDetails'
                  )
              }
            ]
          },
          {
            path: 'rest',
            component: () =>
              import(
                /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
              ),
            children: [
              {
                path: '',
                name: 'rest',
                meta: { requiresAuth: true },
                component: () =>
                  import(/* webpackChunkName: "rests" */ '@/pages/rest/Rests')
              },
              {
                path: 'create',
                name: 'restCreate',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "rest-create" */ '@/pages/rest/RestDetails'
                  )
              },
              {
                path: 'edit/:id',
                name: 'restEdit',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "rest-edit" */ '@/pages/rest/RestDetails'
                  )
              },
              {
                path: 'copy/:id',
                name: 'restCopy',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "rest-copy" */ '@/pages/rest/RestDetails'
                  )
              },
              {
                path: 'restore',
                name: 'restRestore',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "rest-restore" */ '@/pages/rest/RestDetails'
                  )
              }
            ]
          },
          {
            path: 'mcp',
            component: () =>
              import(
                /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
              ),
            children: [
              {
                path: '',
                name: 'mcp',
                meta: { requiresAuth: true },
                component: () =>
                  import(/* webpackChunkName: "mcps" */ '@/pages/mcp/Mcps')
              },
              {
                path: 'create',
                name: 'mcpCreate',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "mcp-create" */ '@/pages/mcp/McpDetails'
                  )
              },
              {
                path: 'edit/:id',
                name: 'mcpEdit',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "mcp-edit" */ '@/pages/mcp/McpDetails'
                  )
              },
              {
                path: 'copy/:id',
                name: 'mcpCopy',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "mcp-copy" */ '@/pages/mcp/McpDetails'
                  )
              },
              {
                path: 'restore',
                name: 'mcpRestore',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "mcp-restore" */ '@/pages/mcp/McpDetails'
                  )
              }
            ]
          },
          {
            path: 'messaging',
            component: () =>
              import(
                /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
              ),
            children: [
              {
                path: '',
                name: 'messaging',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "messagings" */ '@/pages/trigger-messaging/Messaging'
                  )
              },
              {
                path: 'create',
                name: 'messagingCreate',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "messaging-create" */ '@/pages/trigger-messaging/MessagingDetails'
                  )
              },
              {
                path: 'edit/:id',
                name: 'messagingEdit',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "messaging-edit" */ '@/pages/trigger-messaging/MessagingDetails'
                  )
              },
              {
                path: 'copy/:id',
                name: 'messagingCopy',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "messaging-copy" */ '@/pages/trigger-messaging/MessagingDetails'
                  )
              },
              {
                path: 'restore',
                name: 'messagingRestore',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "messaging-restore" */ '@/pages/trigger-messaging/MessagingDetails'
                  )
              }
            ]
          },
          {
            path: 'event-handler',
            component: () =>
              import(
                /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
              ),
            children: [
              {
                path: '',
                name: 'eventHandler',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "event-handlers" */ '@/pages/trigger-event-handler/EventHandler'
                  )
              },
              {
                path: 'create',
                name: 'eventHandlerCreate',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "event-handler-create" */ '@/pages/trigger-event-handler/EventHandlerDetails'
                  )
              },
              {
                path: 'edit/:id',
                name: 'eventHandlerEdit',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "event-handler-edit" */ '@/pages/trigger-event-handler/EventHandlerDetails'
                  )
              },
              {
                path: 'copy/:id',
                name: 'eventHandlerCopy',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "event-handler-copy" */ '@/pages/trigger-event-handler/EventHandlerDetails'
                  )
              },
              {
                path: 'restore',
                name: 'eventHandlerRestore',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "event-handler-restore" */ '@/pages/trigger-event-handler/EventHandlerDetails'
                  )
              }
            ]
          },
          {
            path: 'ui',
            component: () =>
              import(
                /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
              ),
            children: [
              {
                path: '',
                name: 'uiTrigger',
                meta: { requiresAuth: true },
                component: () =>
                  import(/* webpackChunkName: "ui-triggers" */ '@/pages/ui-trigger/UiTriggers')
              },
              {
                path: 'create',
                name: 'uiTriggerCreate',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "ui-trigger-create" */ '@/pages/ui-trigger/UiTriggerDetails'
                  )
              },
              {
                path: 'edit/:id',
                name: 'uiTriggerEdit',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "ui-trigger-edit" */ '@/pages/ui-trigger/UiTriggerDetails'
                  )
              },
              {
                path: 'copy/:id',
                name: 'uiTriggerCopy',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "ui-trigger-copy" */ '@/pages/ui-trigger/UiTriggerDetails'
                  )
              },
              {
                path: 'restore',
                name: 'uiTriggerRestore',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "ui-trigger-restore" */ '@/pages/ui-trigger/UiTriggerDetails'
                  )
              }
            ]
          },
          {
            path: 'whatsapp',
            component: () =>
              import(
                /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
              ),
            children: [
              {
                path: '',
                name: 'whatsapp',
                meta: { requiresAuth: true },
                component: () =>
                  import(/* webpackChunkName: "whatsapps" */ '@/pages/trigger-whatsapp/Whatsapp')
              },
              {
                path: 'create',
                name: 'whatsappCreate',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "whatsapp-create" */ '@/pages/trigger-whatsapp/WhatsappDetails'
                  )
              },
              {
                path: 'edit/:id',
                name: 'whatsappEdit',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "whatsapp-edit" */ '@/pages/trigger-whatsapp/WhatsappDetails'
                  )
              },
              {
                path: 'copy/:id',
                name: 'whatsappCopy',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "whatsapp-copy" */ '@/pages/trigger-whatsapp/WhatsappDetails'
                  )
              },
              {
                path: 'restore',
                name: 'whatsappRestore',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "whatsapp-restore" */ '@/pages/trigger-whatsapp/WhatsappDetails'
                  )
              }
            ]
          }
        ]
      },
      {
        path: 'logs-steps',
        component: () =>
          import(
            /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
          ),
        children: [
          {
            path: '',
            name: 'logs-steps',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "logs-steps" */ '@/pages/logs-steps/LogsSteps'
              )
          }
        ]
      },
      {
        path: 'logs-events',
        component: () =>
          import(
            /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
          ),
        children: [
          {
            path: '',
            name: 'logs-events',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "logs-events" */ '@/pages/logs-events/LogsEvents'
              )
          }
        ]
      },
      {
        path: 'logs-internal-jobs',
        component: () =>
          import(
            /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
          ),
        children: [
          {
            path: '',
            name: 'logs-internal-jobs',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "logs-internal-jobs" */ '@/pages/logs-internal-jobs/LogsInternalJobs'
              )
          }
        ]
      },
      {
        path: 'logs-telemetry',
        component: () =>
          import(
            /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
          ),
        children: [
          {
            path: '',
            name: 'logs-telemetry',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "logs-telemetry" */ '@/pages/logs-telemetry/Telemetries'
              )
          }
        ]
      },
      {
        path: 'logs-instances',
        component: () =>
          import(
            /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
          ),
        children: [
          {
            path: '',
            name: 'logs-instances',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "instances" */ '@/pages/logs-instances/LogsInstances'
              )
          },
          {
            path: 'stats/:id',
            name: 'logInstanceStats',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "user-edit" */ '@/pages/logs-instances/LogsInstancesDetails'
              )
          }
        ]
      },
      {
        path: 'access',
        component: () =>
          import(
            /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
          ),
        children: [
          {
            path: 'users',
            component: () =>
              import(
                /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
              ),
            children: [
              {
                path: '',
                name: 'users',
                meta: { requiresAuth: true },
                component: () =>
                  import(/* webpackChunkName: "users" */ '@/pages/users/Users')
              },
              {
                path: 'create',
                name: 'userCreate',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "user-create" */ '@/pages/users/UserDetails'
                  )
              },
              {
                path: 'edit/:id',
                name: 'userEdit',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "user-edit" */ '@/pages/users/UserDetails'
                  )
              }
            ]
          },
          {
            path: 'roles',
            component: () =>
              import(
                /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
              ),
            children: [
              {
                path: '',
                name: 'roles',
                meta: { requiresAuth: true },
                component: () =>
                  import(/* webpackChunkName: "roles" */ '@/pages/roles/Roles')
              },
              {
                path: 'create',
                name: 'roleCreate',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "role-create" */ '@/pages/roles/RoleDetails'
                  )
              },
              {
                path: 'edit/:id',
                name: 'roleEdit',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "role-edit" */ '@/pages/roles/RoleDetails'
                  )
              },
              {
                path: 'copy/:id',
                name: 'roleCopy',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "role-copy" */ '@/pages/roles/RoleDetails'
                  )
              }
            ]
          },
          {
            path: 'tenants',
            component: () =>
              import(
                /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
              ),
            children: [
              {
                path: '',
                name: 'tenants',
                meta: { requiresAuth: true },
                component: () =>
                  import(/* webpackChunkName: "tenants" */ '@/pages/tenants/Tenants')
              },
              {
                path: 'create',
                name: 'tenantCreate',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "tenant-create" */ '@/pages/tenants/TenantDetails'
                  )
              },
              {
                path: 'edit/:id',
                name: 'tenantEdit',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "tenant-edit" */ '@/pages/tenants/TenantDetails'
                  )
              },
              {
                path: 'copy/:id',
                name: 'tenantCopy',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "tenant-copy" */ '@/pages/tenants/TenantDetails'
                  )
              }
            ]
          }
        ]
      },
      {
        path: 'templates',
        component: () =>
          import(
            /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
          ),
        children: [
          {
            path: 'templates',
            component: () =>
              import(
                /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
              ),
            children: [
              {
                path: '',
                name: 'templates',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "templates" */ '@/pages/templates/Templates'
                  )
              },
              {
                path: 'create',
                name: 'templateCreate',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "template-create" */ '@/pages/templates/TemplateDetails'
                  )
              },
              {
                path: 'edit/:id',
                name: 'templateEdit',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "templates-edit" */ '@/pages/templates/TemplateDetails'
                  )
              },
              {
                path: 'copy/:id',
                name: 'templateCopy',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "templates-copy" */ '@/pages/templates/TemplateDetails'
                  )
              },
              {
                path: 'restore',
                name: 'templateRestore',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "templates-restore" */ '@/pages/templates/TemplateDetails'
                  )
              }
            ]
          },
          {
            path: 'translations',
            component: () =>
              import(
                /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
              ),
            children: [
              {
                path: '',
                name: 'translations',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "translations" */ '@/pages/translations/Translations'
                  )
              },
              {
                path: 'create',
                name: 'translationCreate',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "translation-create" */ '@/pages/translations/TranslationDetails'
                  )
              },
              {
                path: 'edit/:id',
                name: 'translationEdit',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "translation-edit" */ '@/pages/translations/TranslationDetails'
                  )
              },
              {
                path: 'copy/:id',
                name: 'translationCopy',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "translation-copy" */ '@/pages/translations/TranslationDetails'
                  )
              },
              {
                path: 'restore',
                name: 'translationRestore',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "translation-restore" */ '@/pages/translations/TranslationDetails'
                  )
              }
            ]
          }
        ]
      },
      {
        path: 'settings',
        component: () =>
          import(
            /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
          ),
        children: [
          {
            path: 'frontend',
            name: 'frontend',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "settings-frontend" */ '@/pages/settings/Settings'
              )
          },
          {
            path: 'instance-processing',
            name: 'instance-processing',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "settings-instance-processing" */ '@/pages/settings/Settings'
              )
          },
          {
            path: 'instance-settings',
            name: 'instance-settings',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "settings-instance-settings" */ '@/pages/settings/Settings'
              )
          },
          {
            path: 'process',
            name: 'process',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "settings-process" */ '@/pages/settings/Settings'
              )
          },
          {
            path: 'plugin',
            name: 'plugin',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "settings-plugin" */ '@/pages/settings/Settings'
              )
          },
          {
            path: 'create/:type',
            name: 'settingCreate',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "setting-create" */ '@/pages/settings/SettingDetails'
              )
          },
          {
            path: 'edit/:id',
            name: 'settingEdit',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "setting-edit" */ '@/pages/settings/SettingDetails'
              )
          },
          {
            path: 'restore/:type',
            name: 'settingRestore',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "setting-restore" */ '@/pages/settings/SettingDetails'
              )
          }
        ]
      },
      {
        path: 'validations',
        component: () =>
          import(
            /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
          ),
        children: [
          {
            path: '',
            name: 'validations',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "validations" */ '@/pages/validation/Validations'
              )
          },
          {
            path: 'create',
            name: 'validationCreate',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "validation-create" */ '@/pages/validation/ValidationDetails'
              )
          },
          {
            path: 'edit/:id',
            name: 'validationEdit',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "validation-edit" */ '@/pages/validation/ValidationDetails'
              )
          },
          {
            path: 'copy/:id',
            name: 'validationCopy',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "validation-copy" */ '@/pages/validation/ValidationDetails'
              )
          },
          {
            path: 'restore',
            name: 'validationRestore',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "validation-restore" */ '@/pages/validation/ValidationDetails'
              )
          }
        ]
      },
      {
        path: 'global-settings',
        component: () =>
          import(
            /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
          ),
        children: [
          {
            path: '',
            name: 'globalSettings',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "global-settings" */ '@/pages/global-settings/GlobalSettings'
              )
          },
          {
            path: 'create',
            name: 'globalSettingsCreate',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "global-settings-create" */ '@/pages/global-settings/GlobalSettingDetails'
              )
          },
          {
            path: 'edit/:id',
            name: 'globalSettingsEdit',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "global-settings-edit" */ '@/pages/global-settings/GlobalSettingDetails'
              )
          }
        ]
      },
      {
        path: 'storage',
        component: () =>
          import(
            /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
          ),
        children: [
          {
            path: '',
            name: 'storage',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "validations" */ '@/pages/storage/Storage'
              )
          },
          {
            path: 'create',
            name: 'storageCreate',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "validation-create" */ '@/pages/storage/StorageDetails'
              )
          },
          {
            path: 'edit/:id',
            name: 'storageEdit',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "validation-edit" */ '@/pages/storage/StorageDetails'
              )
          }
        ]
      },
      {
        path: 'static-resources',
        component: () =>
          import(
            /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
          ),
        children: [
          {
            path: '',
            name: 'static-resources',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "static-resources" */ '@/pages/static-resources/StaticResources'
              )
          },
          {
            path: 'create',
            name: 'staticResourcesCreate',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "static-resource-create" */ '@/pages/static-resources/StaticResourcesDetails'
              )
          },
          {
            path: 'edit/:id',
            name: 'staticResourcesEdit',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "static-resource-edit" */ '@/pages/static-resources/StaticResourcesDetails'
              )
          }
        ]
      },
      {
        path: 'libraries',
        component: () =>
          import(
            /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
          ),
        children: [
          {
            path: '',
            name: 'libraries',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "libraries" */ '@/pages/libraries/Libraries'
              )
          },
          {
            path: 'create',
            name: 'libraryCreate',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "library-create" */ '@/pages/libraries/LibraryDetails'
              )
          },
          {
            path: 'edit/:id',
            name: 'libraryEdit',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "library-edit" */ '@/pages/libraries/LibraryDetails'
              )
          },
          {
            path: 'copy/:id',
            name: 'libraryCopy',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "library-copy" */ '@/pages/libraries/LibraryDetails'
              )
          },
          {
            path: 'restore',
            name: 'libraryRestore',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "library-restore" */ '@/pages/libraries/LibraryDetails'
              )
          }
        ]
      },
      {
        path: 'entities',
        component: () =>
          import(
            /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
          ),
        children: [
          {
            path: '',
            name: 'entities',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "entities" */ '@/pages/entities/Entities'
              )
          },
          {
            path: 'create',
            name: 'entityCreate',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "entity-create" */ '@/pages/entities/EntitiesDetails'
              )
          },
          {
            path: 'generate',
            name: 'entityGenerate',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "entity-generate" */ '@/pages/entities/EntityGeneration'
              )
          },
          {
            path: 'edit/:id',
            name: 'entityEdit',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "entity-edit" */ '@/pages/entities/EntitiesDetails'
              )
          },
          {
            path: 'copy/:id',
            name: 'entityCopy',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "entity-copy" */ '@/pages/entities/EntitiesDetails'
              )
          },
          {
            path: 'restore',
            name: 'entityRestore',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "entity-restore" */ '@/pages/entities/EntitiesDetails'
              )
          }
        ]
      },
      {
        path: 'tags',
        component: () =>
          import(
            /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
          ),
        children: [
          {
            path: '',
            name: 'tags',
            meta: { requiresAuth: true },
            component: () =>
              import(/* webpackChunkName: "tags" */ '@/pages/tags/Tags')
          },
          {
            path: 'create',
            name: 'tagCreate',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "tag-create" */ '@/pages/tags/TagDetails.vue'
              )
          },
          {
            path: 'edit/:id',
            name: 'tagEdit',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "tag-edit" */ '@/pages/tags/TagDetails.vue'
              )
          },
          {
            path: 'copy/:id',
            name: 'tagCopy',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "tag-copy" */ '@/pages/tags/TagDetails.vue'
              )
          }
        ]
      },
      {
        path: 'modules',
        component: () =>
          import(
            /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
          ),
        children: [
          {
            path: 'local',
            component: () =>
              import(
                /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
              ),
            children: [
              {
                path: '',
                name: 'local',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "modules" */ '@/pages/modules/Modules'
                  )
              },
              {
                path: 'create',
                name: 'moduleCreate',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "module-create" */ '@/pages/modules/ModuleDetails'
                  )
              },
              {
                path: 'edit/:id',
                name: 'moduleEdit',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "module-edit" */ '@/pages/modules/ModuleDetails'
                  )
              },
              {
                path: 'restore',
                name: 'moduleRestore',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "module-restore" */ '@/pages/modules/ModuleDetails'
                  )
              },
              {
                path: 'import',
                name: 'moduleImport',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "module-edit" */ '@/pages/modules/ModuleImport'
                  )
              }
            ]
          },
          {
            path: 'hub',
            component: () =>
              import(
                /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
              ),
            children: [
              {
                path: '',
                name: 'hub',
                meta: { requiresAuth: true },
                component: () =>
                  import(/* webpackChunkName: "hub" */ '@/pages/hub/Hub')
              },
              {
                path: 'edit/:id',
                name: 'hubEdit',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "hub" */ '@/pages/hub/HubDetails.vue'
                  )
              }
            ]
          },
          {
            path: 'import-history',
            component: () =>
              import(
                /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
              ),
            children: [
              {
                path: '',
                name: 'importHistory',
                meta: { requiresAuth: true },
                component: () =>
                  import(/* webpackChunkName: "import-history" */ '@/pages/import-history/ImportHistory')
              },
              {
                path: 'edit/:id',
                name: 'importHistoryEdit',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    /* webpackChunkName: "import-history" */ '@/pages/import-history/ImprtHistoryDetails.vue'
                  )
              }
            ]
          }
        ]
      },
      {
        path: 'gdpr',
        component: () =>
          import(
            /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
          ),
        children: [
          {
            path: '',
            name: 'gdpr',
            meta: { requiresAuth: true },
            component: () =>
              import(/* webpackChunkName: "modules" */ '@/pages/gdpr/Gdpr')
          }
        ]
      },
      {
        path: 'cache',
        component: () =>
          import(
            /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
          ),
        children: [
          {
            path: '',
            name: 'cache',
            meta: { requiresAuth: true },
            component: () =>
              import(/* webpackChunkName: "modules" */ '@/pages/cache/Cache')
          }
        ]
      },
      {
        path: 'browse-data',
        component: () =>
          import(
            /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
          ),
        children: [
          {
            path: '',
            name: 'browseData',
            meta: { requiresAuth: true },
            component: () =>
              import(/* webpackChunkName: "modules" */ '@/pages/browse-data/BrowseData')
          }
        ]
      },
      {
        path: 'plugins',
        component: () =>
          import(
            /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
          ),
        children: [
          {
            path: '',
            name: 'plugins',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "plugins" */ '@/pages/plugins/Plugins'
              )
          },
          {
            path: 'create',
            name: 'pluginCreate',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "plugin-create" */ '@/pages/plugins/PluginDetails'
              )
          },
          {
            path: 'edit/:id',
            name: 'pluginEdit',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "plugin-edit" */ '@/pages/plugins/PluginDetails'
              )
          },
          {
            path: 'copy/:id',
            name: 'pluginCopy',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "plugin-copy" */ '@/pages/plugins/PluginDetails'
              )
          },
          {
            path: 'restore',
            name: 'pluginRestore',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "plugin-restore" */ '@/pages/plugins/PluginDetails'
              )
          }
        ]
      },
      {
        path: 'history',
        component: () =>
          import(
            /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
          ),
        children: [
          {
            path: '',
            name: 'history',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "plugins" */ '@/pages/history/History'
              )
          },
          {
            path: 'edit/:id',
            name: 'historyEdit',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "plugin-edit" */ '@/pages/history/HistoryDetails'
              )
          }
        ]
      },
      {
        path: 'mass-changes',
        component: () =>
          import(
            /* webpackChunkName: "layout-default" */ '@/layouts/EmptyLayout'
          ),
        children: [
          {
            path: '',
            name: 'mass-changes',
            meta: { requiresAuth: true },
            component: () =>
              import(
                /* webpackChunkName: "mass-changes" */ '@/pages/mass-change/MassChange'
              )
          }
        ]
      },
      {
        path: 'terms-and-Conditions',
        name: 'terms',
        component: () =>
          import(
            /* webpackChunkName: "terms-and-Conditions" */ '@/pages/Protection-terms'
          )
      },
      {
        path: 'data-protection-policy',
        name: 'data-protection',
        component: () =>
          import(
            /* webpackChunkName: "data-protection-policy" */ '@/pages/Protection-terms'
          )
      },
      {
        path: ':pathMatch(.*)*',
        component: () =>
          import(
            /* webpackChunkName: "layout-error" */ '@/layouts/NotFoundLayout'
          ),
        children: [
          {
            path: '',
            name: 'error',
            component: () =>
              import(
                /* webpackChunkName: "error" */ '@/pages/error/NotFoundPage'
              )
          }
        ]
      }
    ]
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { left: 0, top: 0 };
  }
});

/**
 * Before each route update
 */

router.beforeEach((to, from, next) => {
  store.dispatch('app/formValid', false)

  if (to?.matched?.some(record => record?.meta?.requiresAuth)) {
    if (!auth.isLoggedIn()) {
      return next({
        path: `/${lang}/login`,
        query: {
          redirectPath: to.path,
          ...Object.keys(to.query || {}).length ? { customQuery: JSON.stringify(to.query) } : {}
        }
      })
    }
    return next()
  }

  return next()
})

/**
 * After each route update
 */
// router.afterEach((to, from) => {
// })

export default router
